import { Trans, useLingui } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  BarChart3,
  Bell,
  Calendar,
  CalendarCheck,
  ChevronRight,
  CreditCard,
  FileText,
  Settings,
  Star,
  UserCog,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { SectionLabel } from "@/components/account/menu-shared";
import { ProviderReviewBanner } from "@/components/account/provider/provider-review-banner";
import { WorkspaceFooterSections } from "@/components/account/workspace-footer-sections";
import { Skeleton } from "@/components/ui/skeleton";
import { useDesktopRedirect } from "@/lib/hooks/use-desktop-redirect";

interface ProviderSidebarData {
  completeness: number;
  ratingAvg: number | null;
  reviewCount: number;
  isVerified: boolean;
}

interface SidebarData {
  provider?: ProviderSidebarData | null;
  actionRequiredNotificationCount?: number;
  informationalNotificationCount?: number;
  pendingAffiliationsCount?: number;
}

interface ProviderMenuItem {
  to: string;
  label: React.ReactNode;
  icon: LucideIcon;
  badge?: string;
  badgeTone?: "muted" | "warning" | "success" | "destructive";
}

export const Route = createFileRoute("/_authenticated/account/provider/")({
  component: ProviderMenuPage,
});

function ProviderMenuPage() {
  const { t } = useLingui();
  useDesktopRedirect("/account/provider/dashboard");

  const { data, isLoading } = useQuery({
    queryKey: ["api", "/api/account/sidebar-data"],
    queryFn: () => apiFetch.get<SidebarData>("/api/account/sidebar-data"),
  });

  const dd = data?.provider ?? null;
  const actionRequired = data?.actionRequiredNotificationCount ?? 0;
  const informational = data?.informationalNotificationCount ?? 0;
  const pendingAffiliations = data?.pendingAffiliationsCount ?? 0;
  const notifTotal = actionRequired + informational;

  const items: ProviderMenuItem[] = [
    { to: "/account/provider/dashboard", label: t`Dashboard`, icon: BarChart3 },
    {
      to: "/account/provider/profile",
      label: t`Service configuration`,
      icon: FileText,
      badge: dd ? `${dd.completeness}%` : undefined,
      badgeTone:
        dd && dd.completeness < 50 ? "warning" : dd && dd.completeness >= 80 ? "success" : "muted",
    },
    { to: "/account/provider/calendar", label: t`Calendar`, icon: Calendar },
    { to: "/account/provider/appointments", label: t`Appointments`, icon: CalendarCheck },
    { to: "/account/provider/patients", label: t`Patients`, icon: Users },
    {
      to: "/account/provider/reviews",
      label: t`Reviews`,
      icon: Star,
      badge: dd?.reviewCount ? String(dd.reviewCount) : undefined,
    },
    { to: "/account/provider/team", label: t`Team`, icon: UserCog },
    {
      to: "/account/provider/notifications",
      label: t`Notifications`,
      icon: Bell,
      badge: notifTotal > 0 ? String(notifTotal) : undefined,
      badgeTone: actionRequired > 0 ? "destructive" : "warning",
    },
    { to: "/account/provider/billing", label: t`Billing`, icon: CreditCard },
    {
      to: "/account/provider/settings",
      label: t`Settings`,
      icon: Settings,
      badge: pendingAffiliations > 0 ? String(pendingAffiliations) : undefined,
      badgeTone: "destructive",
    },
  ];

  return (
    <div>
      <header className="mb-4">
        <h1 className="font-display text-foreground text-2xl font-semibold tracking-[-0.02em]">
          <Trans>Provider workspace</Trans>
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          <Trans>Your practice — profile, calendar, patients, reviews.</Trans>
        </p>
      </header>

      <div className="mb-4 empty:hidden">
        <ProviderReviewBanner />
      </div>

      {isLoading ? (
        <Skeleton className="h-24 w-full rounded-xl" />
      ) : (
        dd && <ProviderStatsCard data={dd} />
      )}

      <SectionLabel>
        <Trans>Account</Trans>
      </SectionLabel>
      <ul className="border-border bg-card divide-y rounded-xl border">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <li key={item.to}>
              <Link
                to={item.to as "/account/provider/dashboard"}
                className="hover:bg-muted flex min-h-[52px] items-center gap-3 px-4 py-3 transition-colors"
              >
                <span className="bg-muted flex size-9 items-center justify-center rounded-lg">
                  <Icon className="text-muted-foreground size-5" />
                </span>
                <span className="text-foreground flex-1 text-sm font-medium">{item.label}</span>
                {item.badge && (
                  <span
                    className={cn(
                      "rounded px-1.5 py-0.5 text-[0.625rem] font-semibold",
                      item.badgeTone === "destructive"
                        ? "bg-destructive text-white"
                        : item.badgeTone === "warning"
                          ? "bg-yellow-500 text-white"
                          : item.badgeTone === "success"
                            ? "bg-emerald-500 text-white"
                            : "bg-primary/10 text-primary",
                    )}
                  >
                    {item.badge}
                  </span>
                )}
                <ChevronRight className="text-muted-foreground/50 size-4" />
              </Link>
            </li>
          );
        })}
      </ul>

      <WorkspaceFooterSections />
    </div>
  );
}

function ProviderStatsCard({ data: dd }: { data: ProviderSidebarData }) {
  const completenessTone =
    dd.completeness >= 80
      ? "bg-emerald-500"
      : dd.completeness >= 50
        ? "bg-teal-500"
        : "bg-yellow-500";

  return (
    <div className="border-border bg-card rounded-xl border p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {dd.ratingAvg !== null ? (
            <>
              <Star className="size-4 fill-yellow-400 text-yellow-400" />
              <span className="text-foreground text-sm font-medium">{dd.ratingAvg.toFixed(1)}</span>
              <span className="text-muted-foreground text-xs">({dd.reviewCount})</span>
            </>
          ) : (
            <span className="text-muted-foreground text-xs">
              <Trans>No rating yet</Trans>
            </span>
          )}
        </div>
        {dd.isVerified && (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
            <Trans>Verified</Trans>
          </span>
        )}
      </div>
      <div className="mt-2.5 flex items-center gap-2">
        <div className="bg-muted h-2 flex-1 overflow-hidden rounded-full">
          <div
            className={cn("h-full rounded-full transition-all", completenessTone)}
            style={{ width: `${dd.completeness}%` }}
          />
        </div>
        <span className="text-muted-foreground text-xs font-medium">{dd.completeness}%</span>
      </div>
    </div>
  );
}
